/**
 * Storage path utilities for 360Builder
 * Defines path templates for the new bucket structure:
 * - staging: Work in progress files
 * - exports: Published content
 *
 * Path Structure:
 * - {workspaceKey}/{projectKey}/{panoramaName}__{filename}
 * - {workspaceKey}/{projectKey}/auto-generated/{panoramaName}__{filename}
 * - {workspaceKey}/shared/{type}/{filename}
 * - global/{type}/{filename}
 *
 * Workspace Key (preferred):
 * - {email-local-part}_{email-domain-without-tld}/{workspace-slug}
 * - Example: info_company/my-workspace
 *
 * Project Key (preferred):
 * - project custom slug (if set), else slugified project name
 *
 * Fallback (legacy):
 * - {craftspaceId}/{projectId} when workspace/project metadata is unavailable
 *
 * Craftspace ID Format: ws + 9 random lowercase characters (e.g., ws0apj57v12)
 * Project ID Format: prj + 9 random characters (e.g., prj9phmr88sg)
 */

/**
 * Generate a random string of specified length
 */
const generateRandomString = (length: number): string => {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate a unique craftspace ID
 * Format: ws + 9 random lowercase characters
 */
export const generateCraftspaceId = (): string => {
  return `ws${generateRandomString(9)}`;
};

/**
 * Generate a unique project ID
 * Format: prj + 9 random characters
 */
export const generateProjectId = (): string => {
  return `prj${generateRandomString(9)}`;
};

const normalizeSegment = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const normalizeEmailKey = (email?: string | null, userId?: string | null): string | null => {
  if (!email || typeof email !== "string") return null;
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.indexOf("@");
  if (atIndex <= 0 || atIndex === trimmed.length - 1) return null;
  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (!domain) return null;
  const domainParts = domain.split(".").filter(Boolean);
  if (domainParts.length === 0) return null;
  const domainCore =
    domainParts.length > 1 ? domainParts[domainParts.length - 2] : domainParts[0];
  const localNorm = normalizeSegment(local.replace(/[+-]/g, "-"));
  const domainNorm = normalizeSegment(domainCore.replace(/[+-]/g, "-"));
  if (!localNorm || !domainNorm) return null;
  let suffix = "";
  if (userId) {
    const compact = String(userId).replace(/[^a-z0-9]/gi, "").toLowerCase();
    if (compact) suffix = compact.slice(-6);
  }
  return suffix ? `${localNorm}_${domainNorm}_${suffix}` : `${localNorm}_${domainNorm}`;
};

const getProjectSlugFromData = (
  projectData?: unknown,
  projectName?: string | null,
): string | null => {
  const data =
    projectData && typeof projectData === "object"
      ? (projectData as Record<string, unknown>)
      : null;
  const rawSlug = typeof data?.customSlug === "string" ? data.customSlug.trim() : "";
  const fromSlug = rawSlug ? normalizeSegment(rawSlug) : "";
  if (fromSlug) return fromSlug;
  if (projectName) {
    const fromName = normalizeSegment(projectName);
    if (fromName) return fromName;
  }
  return null;
};

const parseStoragePath = (
  storagePath?: string | null,
): { craftspaceKey: string; projectKey: string } | null => {
  if (!storagePath || typeof storagePath !== "string") return null;
  const clean = storagePath.replace(/^\/+|\/+$/g, "");
  if (!clean) return null;
  const parts = clean.split("/").filter(Boolean);
  if (parts.length < 3) return null;
  return {
    craftspaceKey: `${parts[0]}/${parts[1]}`,
    projectKey: parts[2],
  };
};

export type StoragePathSegments = {
  craftspaceKey: string;
  projectKey: string;
};

export const resolveStoragePathSegments = (args: {
  craftspaceId: string;
  projectId: string;
  projectName?: string | null;
  projectData?: unknown;
  userEmail?: string | null;
  userId?: string | null;
  workspaceSlug?: string | null;
  storagePath?: string | null;
}): StoragePathSegments => {
  const parsed = parseStoragePath(args.storagePath);
  if (parsed) {
    return parsed;
  }

  const emailKey = normalizeEmailKey(args.userEmail, args.userId);
  const workspaceSlug = args.workspaceSlug ? normalizeSegment(args.workspaceSlug) : "";
  const projectSlug = getProjectSlugFromData(args.projectData, args.projectName);

  if (emailKey && workspaceSlug) {
    return {
      craftspaceKey: `${emailKey}/${workspaceSlug}`,
      projectKey: projectSlug || args.projectId,
    };
  }

  return { craftspaceKey: args.craftspaceId, projectKey: args.projectId };
};

export const StoragePaths = {
  // Private bucket paths (work in progress)
  private: {
    // Panorama paths - flat project folder with panoramaName-prefixed filenames
    panoramaOriginal: (
      craftspaceId: string,
      projectId: string,
      panoramaName: string,
      filename: string,
    ) => `${craftspaceId}/${projectId}/${panoramaName}__${filename}`,

    panoramaProcessed: (
      craftspaceId: string,
      projectId: string,
      panoramaName: string,
      filename: string,
    ) => `${craftspaceId}/${projectId}/auto-generated/${panoramaName}__${filename}`,

    panoramaMetadata: (
      craftspaceId: string,
      projectId: string,
      panoramaName: string,
      filename: string,
    ) => `${craftspaceId}/${projectId}/auto-generated/${panoramaName}__${filename}`,

    // Thumbnail paths - now within panorama folder
    thumbnail: (craftspaceId: string, projectId: string, panoramaName: string, filename: string) =>
      `${craftspaceId}/${projectId}/auto-generated/${panoramaName}__${filename}`,

    // Hotspot paths
    hotspot: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/hotspot_${filename}`,

    // Skin paths
    skin: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/skin_${filename}`,

    // CMS paths
    cms: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/cms_${filename}`,

    // Export paths
    export: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/export_${filename}`,

    // Versioned asset paths
    assetVersion: (
      craftspaceId: string,
      projectId: string,
      entityType: string,
      entityId: string,
      versionNumber: number,
      filename: string,
    ) =>
      `${craftspaceId}/${projectId}/version_${entityType}_${entityId}_v${versionNumber}_${filename}`,

    // Trash paths - new project-local structure
    trash: {
      panorama: (craftspaceId: string, projectId: string, panoramaName: string, filename: string) =>
        `${craftspaceId}/${projectId}/trash/panoramas/${panoramaName}/${filename}`,
      metadata: (craftspaceId: string, projectId: string, panoramaName: string) =>
        `${craftspaceId}/${projectId}/trash/panoramas/${panoramaName}/_trash_metadata.json`,
      root: (craftspaceId: string, projectId: string) => `${craftspaceId}/${projectId}/trash`,
    },

    // Shared craftspace paths
    shared: {
      template: (craftspaceId: string, filename: string) =>
        `${craftspaceId}/shared/templates/${filename}`,
      component: (craftspaceId: string, filename: string) =>
        `${craftspaceId}/shared/components/${filename}`,
      asset: (craftspaceId: string, filename: string) => `${craftspaceId}/shared/assets/${filename}`,
    },

    // Temporary paths
    temp: {
      upload: (craftspaceId: string, filename: string) => `${craftspaceId}/temp/uploads/${filename}`,
      processing: (craftspaceId: string, filename: string) =>
        `${craftspaceId}/temp/processing/${filename}`,
    },

    // Global paths
    global: {
      template: (filename: string) => `global/templates/${filename}`,
      component: (filename: string) => `global/components/${filename}`,
      asset: (filename: string) => `global/assets/${filename}`,
    },

    // Global temp paths
    globalTemp: {
      upload: (filename: string) => `temp/uploads/${filename}`,
      processing: (filename: string) => `temp/processing/${filename}`,
    },
  },

  // Public bucket paths (published content)
  public: {
    // Panorama paths
    panorama: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/${filename}`,

    // Thumbnail paths
    thumbnail: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/auto-generated/${filename}`,

    // Asset paths
    asset: (craftspaceId: string, projectId: string, filename: string) =>
      `${craftspaceId}/${projectId}/${filename}`,

    // Shared craftspace paths
    shared: {
      component: (craftspaceId: string, filename: string) =>
        `${craftspaceId}/shared/components/${filename}`,
    },

    // Global paths
    global: {
      component: (filename: string) => `global/components/${filename}`,
    },
  },
};

/**
 * Get the appropriate bucket name based on the file type and context
 */
export const getBucketName = (isPublic: boolean = false): string => {
  return isPublic ? "exports" : "staging";
};

/**
 * Get or create craftspace ID for a user
 * This should be stored in the database and retrieved, but for now we'll generate it
 */
export const getCraftspaceId = (userId: string): string => {
  // TODO: In the future, this should fetch from database
  // For now, we'll generate a deterministic ID based on userId
  const hash = userId
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase()
    .substring(0, 9);
  return `ws${hash.padEnd(9, "0")}`;
};

/**
 * Get project ID from project UUID
 * This should use the actual project ID from the database
 */
export const getProjectId = (projectUuid: string): string => {
  // TODO: In the future, this should fetch from database
  // For now, we'll generate a deterministic ID based on projectUuid
  const hash = projectUuid
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase()
    .substring(0, 9);
  return `prj${hash.padEnd(9, "0")}`;
};
